import React, {useEffect, useState} from "react";
import {
    calculateAgriculturalValue,
    calculateAppraisedValue,
    calculateAssessedValue,
    calculateLandTotalValue, jsonCloneWithDates,
} from "../../../../helpers/utils/OrderDetailUtils";
import {useGetEnumsQuery} from "../../../../app/globalApi";
import {getCurrentTaxYear} from "../../../../helpers/utils/Utils";
import {
    assessmentOverrideFormatting,
    assessmentsAgOverrideFormatting,
    assessmentsAgRowFormatting,
     exemptionsList,
    isAgExemptionExist
} from "../TaxCertificate/TaxCertNationalUtil";
import { AssessmentYearRow } from "../TaxCertificate/UtilComponents/AssessmentYearRow";
import AssessmentsRowFormatting from "../TaxCertificate/UtilComponents/AssessmentsRowFormatting";

interface AssessmentInfoSectionProps {
    curAccount: any;
    setCertificate: any;
    certificate: any;
    setName: any;
    setIsSavedResult: any;
    editable: boolean;
    setProductTypeInvisible: any;
    setRender: any;
    setUpdatedAccountIndexList: any;
    showAccount: number;
}

const AssessmentInfoSection: React.FC<AssessmentInfoSectionProps> = ({
                                                                         curAccount,
                                                                         setCertificate,
                                                                         certificate,
                                                                         setName,
                                                                         setIsSavedResult,
                                                                         editable,
                                                                         setProductTypeInvisible,
                                                                         setRender,
                                                                         setUpdatedAccountIndexList,
                                                                         showAccount,
                                                                     }) => {
    const [overrideValue, setOverrideValue] = useState<boolean>(false);
    const [yearHasAg, setYearHasAg] = useState<boolean | null>(null);
    const {data: enums} = useGetEnumsQuery("");
    const assessment = certificate?.accounts?.[showAccount]?.assessment;
    const certified = assessment?.['certified'] === true;

    useEffect(() => {
        if (yearHasAg || overrideValue) {
            setUpdatedAccountIndexList((prev: any) => [...prev, showAccount]);
        }
    }, [yearHasAg, overrideValue]);

    const recalculateForAgChanges = (curAccount: any) => {
        const originalAssessment = curAccount?.assessment;
        calculateAgriculturalValue(showAccount, originalAssessment, setRender, setCertificate);
        calculateLandTotalValue(showAccount, originalAssessment, setRender, setCertificate);
        calculateAssessedValue(showAccount, originalAssessment, setRender, setCertificate);
        calculateAppraisedValue(showAccount, originalAssessment, setRender, setCertificate);
    };

    useEffect(() => {
        if (!overrideValue) return;
        recalculateForAgChanges(curAccount);
        setIsSavedResult(false);

        setOverrideValue(false);
    }, [overrideValue]);

    const assessmentJson = JSON.stringify(curAccount?.assessment);

    useEffect(() => {
        if (!curAccount?.assessment) return;
        setYearHasAg(isAgExemptionExist(curAccount));
    }, [assessmentJson]);

    const clearAgValues = (curAccount: any) => {
        const updatedAssessment = {
            ...curAccount.assessment,
            agTotal: null,
            agMarketValue: null,
            agTimberValue: null,
            agLossValue: null,
            overrideAgValue: null,
            overrideLandTotalValue: null,
        };

        const updatedAccount = {
            ...curAccount,
            assessment: updatedAssessment,
        };

        const updatedAccounts = [...certificate.accounts];
        updatedAccounts[showAccount] = updatedAccount;

        setCertificate({
            ...certificate,
            accounts: updatedAccounts,
        });
    };

    useEffect(() => {
        if (yearHasAg === false) {
            clearAgValues(curAccount);
            recalculateForAgChanges(curAccount);
        }
    }, [yearHasAg]);

    return (
        <div className="h-full">
            <div className="border-l border-highlight-5 h-full property_info table_data">
                <table className="text-sm w-full [&_td]:border [&_td]:border-surface-5 p-[6px] text-left account_information">
                    <thead>
                    <tr>
                        <th className="bg-highlight-5 text-text-0 relative pl-2 whitespace-nowrap">Assessment Info</th>
                        <th className="bg-highlight-5 text-text-0 relative pl-2 text-center">

                            <AssessmentYearRow
                                curAccount={curAccount}
                                setCertificate={setCertificate}
                                showAccount={showAccount}
                                name={'taxYear'}
                                setName={setName}
                                editable={editable}
                                setIsSavedResult={setIsSavedResult}

                            />
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td className="first_td pl-3">Land</td>
                        <td>
                            <AssessmentsRowFormatting
                                setCertificate={setCertificate}
                                certificate={certificate}
                                showAccount={showAccount}
                                name="landMarketValue"
                                setName={setName}
                                setProductTypeInvisible={setProductTypeInvisible}
                                editable={editable}
                                setIsSavedResult={setIsSavedResult}
                            />

                        </td>
                    </tr>

                    {yearHasAg && (
                        <>
                            <tr>
                                <td className="first_td pl-3">Ag Market</td>
                                <td>
                                    {assessmentsAgRowFormatting(
                                        setOverrideValue,
                                        curAccount,
                                        editable,
                                        certificate,
                                        setCertificate,
                                        "agMarketValue",
                                        setIsSavedResult,
                                        showAccount,
                                        setUpdatedAccountIndexList
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="first_td pl-3">Ag Timber</td>
                                <td>
                                    {assessmentsAgRowFormatting(
                                        setOverrideValue,
                                        curAccount,
                                        editable,
                                        certificate,
                                        setCertificate,
                                        "agTimberValue",
                                        setIsSavedResult,
                                        showAccount,
                                        setUpdatedAccountIndexList
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="first_td pl-3">Ag Loss</td>
                                <td>
                                    {assessmentsAgRowFormatting(
                                        setOverrideValue,
                                        curAccount,
                                        editable,
                                        certificate,
                                        setCertificate,
                                        "agLossValue",
                                        setIsSavedResult,
                                        showAccount,
                                        setUpdatedAccountIndexList
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="first_td pl-3">Ag Total</td>
                                <td title={'(AgMarketValue + AgTimberValue) - AgLossValue'}>
                                    {assessmentsAgOverrideFormatting(
                                        certificate,
                                        setCertificate,
                                        showAccount,
                                        "agriculturalValue",
                                        setName,
                                        setProductTypeInvisible,
                                        editable,
                                        'overrideAgValue',
                                        setIsSavedResult,
                                        setRender
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td className="first_td pl-3">Land Total</td>
                                <td title={'LandMarketValue + (AgMarketValue + AgTimberValue)'}>
                                    {assessmentsAgOverrideFormatting(
                                        certificate,
                                        setCertificate,
                                        showAccount,
                                        "landTotalValue",
                                        setName,
                                        setProductTypeInvisible,
                                        editable,
                                        'overrideLandTotalValue',
                                        setIsSavedResult,
                                        setRender
                                    )}
                                </td>
                            </tr>
                        </>
                    )}

                    <tr>
                        <td className="pl-3">Improvement</td>
                        <td>
                            <AssessmentsRowFormatting
                                setCertificate={setCertificate}
                                certificate={certificate}
                                showAccount={showAccount}
                                name="improvementMarketValue"
                                setName={setName}
                                setProductTypeInvisible={setProductTypeInvisible}
                                editable={editable}
                                setIsSavedResult={setIsSavedResult}
                            />

                        </td>
                    </tr>

                    <tr className="readonly_bg">
                        <td className="pl-3">Appraised</td>
                        <td title={'LandTotal + ImprovementMarketValue'}>

                            {assessmentOverrideFormatting(
                                curAccount,
                                'appraisedValue',
                                setName,
                                setProductTypeInvisible,
                                editable,
                                "overrideAppraisedValue",
                                setIsSavedResult,
                                setRender,
                                showAccount,
                                setUpdatedAccountIndexList,
                                setCertificate,
                                certificate
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td className="pl-3">
                            Other Exemption
                        </td>
                        <td>
                            <AssessmentsRowFormatting
                                setCertificate={setCertificate}
                                certificate={certificate}
                                showAccount={showAccount}
                                name="otherExemption"
                                setName={setName}
                                setProductTypeInvisible={setProductTypeInvisible}
                                editable={editable}
                                setIsSavedResult={setIsSavedResult}
                            />
                        </td>
                    </tr>

                    <tr className="readonly_bg">
                        <td className="pl-3">Assessed</td>
                        <td title={'(AgTotal + LandMarketValue + ImprovementMarketValue) - HomesteadCap'}>
                            {assessmentOverrideFormatting(
                                curAccount,
                                'assessedValue',
                                setName,
                                setProductTypeInvisible,
                                editable,
                                "overrideAssessedValue",
                                setIsSavedResult,
                                setRender,
                                showAccount,
                                setUpdatedAccountIndexList,
                                setCertificate,
                                certificate
                            )}
                        </td>
                    </tr>

                    <tr>
                        <td className="pl-3">Exemptions</td>
                        <td className="right-align">
                            {exemptionsList(
                                !editable ? undefined : Array.isArray(enums?.["Exemption"]) && enums?.["Exemption"].length > 0 && enums?.["Exemption"],
                                "value",
                                "value",
                                "Select ...",
                                true,
                                true,
                                curAccount,
                                setProductTypeInvisible,
                                setIsSavedResult,
                                showAccount,
                                setUpdatedAccountIndexList
                            )}
                        </td>
                    </tr>

                    <tr className="readonly_bg">
                        <td className="pl-3">Certified</td>
                        <td>
                            <input
                                className="ml-2"
                                type="checkbox"
                                checked={certified}
                                onClick={(e) => {
                                    const target = e.target as HTMLInputElement;
                                    if (editable && assessment) {
                                        const updatedCertificate = jsonCloneWithDates(certificate);
                                        updatedCertificate.accounts[showAccount].assessment.certified = target.checked;

                                        setCertificate(updatedCertificate);
                                        setIsSavedResult(false);

                                        setUpdatedAccountIndexList((prev: any) =>
                                            prev.includes(showAccount) ? prev : [...prev, showAccount]
                                        );
                                    }
                                }}
                                disabled={!editable}
                            />
                        </td>
                    </tr>

                    {(curAccount?.assessment?.taxYear >= getCurrentTaxYear() && curAccount?.assessment?.['certified'] === false) ? (
                        <tr className="readonly_bg">
                            <td colSpan={2} style={{fontSize: '14px'}}>
                                * NOTE: When calculating the Estimate W/O Exemption amounts for tax years where
                                the Tax Value and/or the Tax Rate are Preliminary, this Estimate W/O Exemption
                                amount is based on a combination of Preliminary Values for this tax year times
                                the last Certified years Tax Rate, so it will not be exact final amount, and is
                                provided for your information only
                            </td>
                        </tr>
                    ) : (
                        [...Array(2)].map((_, index) => (
                            <tr key={index} className="readonly_bg">
                                <td className="p-[9px] opacity-0">1</td>
                                <td className="p-[9px] opacity-0">1</td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AssessmentInfoSection;